import { useState } from "react";
import { ArrowRight, Braces, ChevronRight, Info } from "lucide-react";
import { Badge } from "../../design-system";
import "./readable-details.css";

export type ReadableField = { label: string; value: string };
export type ReadableChange = { label: string; before: string; after: string };
/** The caller supplies display-ready values. This component does not interpret API payloads. */
export type ReadableDetailView = {
  title: string;
  summary: string;
  fields?: ReadableField[];
  changes?: ReadableChange[];
  nextStep?: string;
  rawText?: string | null;
};

export function RawDebugDetails({
  rawText,
  canRead = false,
}: {
  rawText?: string | null;
  canRead?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!canRead || !rawText) return null;
  return (
    <details
      className="mcrd-original"
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary>
        <ChevronRight size={15} />
        <Braces size={16} />
        <span>원본 보기</span>
        <Badge>관리자 전용</Badge>
      </summary>
      {expanded && (
        <pre tabIndex={0} aria-label="원본 데이터">
          {rawText}
        </pre>
      )}
    </details>
  );
}

export function ReadableDetails({
  view,
  canReadDebug = false,
}: {
  view: ReadableDetailView;
  canReadDebug?: boolean;
}) {
  return (
    <div className="mcrd-details">
      <div className="mcrd-summary">
        <h3>{view.title}</h3>
        <p>{view.summary}</p>
      </div>
      {!!view.fields?.length && (
        <dl className="mcrd-fields">
          {view.fields.map((field) => (
            <div key={field.label}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!!view.changes?.length && (
        <section className="mcrd-changes" aria-label="변경 내역">
          <h4>변경 내역</h4>
          {view.changes.map((change) => (
            <div className="mcrd-change" key={change.label}>
              <strong>{change.label}</strong>
              <div>
                <span>
                  <small>변경 전</small>
                  {change.before}
                </span>
                <ArrowRight size={16} aria-hidden="true" />
                <span>
                  <small>변경 후</small>
                  {change.after}
                </span>
              </div>
            </div>
          ))}
        </section>
      )}
      {view.nextStep && (
        <div className="mcrd-next">
          <Info size={16} />
          <div>
            <strong>확인할 내용</strong>
            <p>{view.nextStep}</p>
          </div>
        </div>
      )}
      <RawDebugDetails
        key={view.rawText}
        rawText={view.rawText}
        canRead={canReadDebug}
      />
    </div>
  );
}
